import React, { useEffect, useMemo, useState } from 'react';
import { Image, Text } from '@msc-b2c/shared-components';
import { TimeSelectionStepProps, TimeslotButtonProps } from '@msc-b2c/interfaces/interfaces';
import { DiningAvailabilityError } from '@msc-b2c/interfaces/enums';
import styles from './timeSelectionStep.module.scss';
import { sortSlotsByTime } from '../../utils/diningAvailabilityUtils';
import { FlowSelectableCard } from '../../components/FlowSelectableCard/FlowSelectableCard';
import {
  getObsPlaceholderImagePath,
  managePlurals,
  replacePlaceholders,
  formatTime,
  useAssetAltText,
  RootState,
} from '@msc-b2c/shared-utils';
import { useSelector } from 'react-redux';

const TimeslotButton: React.FC<TimeslotButtonProps> = ({
  time,
  isAvailable,
  isSelected,
  isConflict = false,
  isInteractionLocked,
  onClick,
}) => {
  const displayTime = formatTime(time);

  return (
    <FlowSelectableCard
      primaryText={displayTime}
      compact
      hideSecondaryOnCompact
      isSelected={isSelected && !isConflict}
      isDisabled={isInteractionLocked || (!isAvailable && !isConflict)}
      isConflict={isConflict}
      onClick={onClick}
    />
  );
};

const replaceLegacyPlaceholders = (
  template: string,
  restaurantName: string,
  allowedQuantity: number
): string => {
  return template
    .replace(/\[Restaurant name\]/gi, restaurantName)
    .replace(/\[n\]/gi, String(allowedQuantity));
};

/**
 * TimeSelectionStep Component
 *
 * Renders restaurant details and available timeslots for AC3:
 *
 * AC3 Features:
 *   - Restaurant image with name & category labels (from ImageTitle)
 *   - Skeleton loader shown while restaurant details load
 *   - Timeslots displayed in chronological order, 12-hour format
 *   - Disabled timeslots show greyed-out state
 *   - Selected timeslot shows blue/highlighted state
 */
export const TimeSelectionStep: React.FC<TimeSelectionStepProps> = ({
  restaurantCode,
  restaurantName,
  restaurantCategory,
  restaurantLimit,
  isLimitReached,
  diningDateTimeProps,
  timeslots,
  conflictTimes = [],
  selectedTime,
  onSelectTime,
  onConflictTimeSelect,
  isLoading,
  availabilityError,
  isSingleSlotLayout,
  isInteractionLocked,
  totalRestaurantsCount,
}) => {
  // Sort timeslots chronologically
  const sortedTimeslots = useMemo(() => {
    return sortSlotsByTime(timeslots || []);
  }, [timeslots]);

  const specialityRestaurantsFromCf = useSelector(
    (state: RootState) => state.myAreaCommonConfig.specialtyRestaurants
  );

  const reastaurantDescriptionFromApi = restaurantName;

  const localizedRestaurantAlt = useAssetAltText(restaurantCode, 'obs');

  const renderableTimeslots = useMemo(() => {
    return sortedTimeslots.filter((slot) => slot.isReserved === false);
  }, [sortedTimeslots]);

  // Keep error mapping explicit and deterministic.
  // If the hook emits an error, use it. Otherwise, only use local restaurant cap.
  const resolvedAvailabilityError =
    availabilityError ?? (isLimitReached ? DiningAvailabilityError.RESTAURANT_LIMIT : undefined);
  const isRestaurantLimitReached =
    resolvedAvailabilityError === DiningAvailabilityError.RESTAURANT_LIMIT;
  const isRestaurantWithAvailability = renderableTimeslots.length > 0;

  const normalizedRestaurantName = restaurantName ?? restaurantCode;
  const normalizedRestaurantLimit = Math.max(1, restaurantLimit ?? 1);

  // Determine error title and text based on error state - simple if/else logic
  let errorTitle: string | undefined;
  let errorText: string | undefined;
  const isRestaurantUnavailable = !isRestaurantLimitReached && !isRestaurantWithAvailability;

  if (isRestaurantLimitReached) {
    const restaurantLimitErrorTitle =
      diningDateTimeProps.maximumReservationsReachedForRestaurantTitle;
    errorTitle = replaceLegacyPlaceholders(
      replacePlaceholders(
        managePlurals(normalizedRestaurantLimit, restaurantLimitErrorTitle || ''),
        {
          restaurantName: normalizedRestaurantName,
          allowedQuantity: String(normalizedRestaurantLimit),
          n: String(normalizedRestaurantLimit),
        }
      ),
      normalizedRestaurantName,
      normalizedRestaurantLimit
    );
    errorText = replacePlaceholders(
      diningDateTimeProps.maximumReservationsReachedForRestaurantLabel,
      {
        restaurantName: normalizedRestaurantName,
        allowedQuantity: String(normalizedRestaurantLimit),
        n: String(normalizedRestaurantLimit),
      }
    );
  } else if (isRestaurantUnavailable) {
    errorTitle = diningDateTimeProps.unavailableLabel;
    errorText =
      diningDateTimeProps.chooseDifferentRestaurantLabel ??
      diningDateTimeProps.maximumReservationsReachedPerPackageLabel;
  }

  // find the CF with roomCode equal to the one returned from API
  const restaurantFromCF = specialityRestaurantsFromCf?.find((item) => {
    return item.roomCode === restaurantCode;
  });

  return (
    <div
      className={`${styles['time-selection']} ${
        isSingleSlotLayout ? styles['time-selection--single-slot'] : ''
      } ${isInteractionLocked ? styles['time-selection--locked'] : ''}`.trim()}
    >
      <div className={styles['time-selection__content']}>
        <div className={styles['time-selection__restaurant-panel']}>
          <div className={styles['time-selection__restaurant-image']}>
            <Image
              src={
                restaurantFromCF?.restaurantImage?.imgDmPath ||
                restaurantFromCF?.restaurantImage?.imgPath ||
                ''
              }
              alt={
                localizedRestaurantAlt ||
                restaurantFromCF?.restaurantName ||
                reastaurantDescriptionFromApi ||
                restaurantCode
              }
              placeholderImagePath={getObsPlaceholderImagePath()}
              styleOption={'cmp-image__overlay'}
            />
          </div>

          <div className={styles['time-selection__restaurant-copy']}>
            <div className={styles['time-selection__restaurant-title']}>
              <Text
                text={restaurantFromCF?.restaurantName || reastaurantDescriptionFromApi || ''}
                styleOption="body-default"
              />
            </div>

            {reastaurantDescriptionFromApi && (
              <div className={styles['time-selection__restaurant-subtitle']}>
                <Text
                  text={reastaurantDescriptionFromApi}
                  styleOption="body-small"
                />
              </div>
            )}
          </div>
        </div>

        {/* AC3: Timeslots grid */}
        <div className={styles['time-selection__timeslots-container']}>
          <div className={styles['time-selection__timeslots-grid']}>
            {errorTitle || errorText ? (
              <div className={styles['time-selection__error']}>
                {errorTitle && (
                  <div className={styles['time-selection__error-title']}>
                    <Text text={errorTitle} />
                  </div>
                )}
                {errorText && (
                  <div className={styles['time-selection__error-text']}>
                    <Text text={errorText} />
                  </div>
                )}
              </div>
            ) : renderableTimeslots.length > 0 ? (
              renderableTimeslots.map((slot) => {
                const isLockedSelectedSlot = isInteractionLocked && selectedTime === slot.time;
                const isConflict = isLockedSelectedSlot ? false : conflictTimes.includes(slot.time);
                const isAvailable = slot.isReserved === false && !isConflict;
                const isSelected = selectedTime === slot.time;

                return (
                  <TimeslotButton
                    key={`timeslot-${restaurantCode}-${slot.time}`}
                    time={slot.time}
                    isAvailable={isAvailable}
                    isSelected={isSelected}
                    isConflict={isConflict}
                    isInteractionLocked={isInteractionLocked}
                    onClick={() => {
                      if (isInteractionLocked) {
                        return;
                      }

                      if (isConflict) {
                        onConflictTimeSelect?.(slot.time);
                        return;
                      }

                      if (isAvailable) {
                        onSelectTime(slot.time);
                      }
                    }}
                  />
                );
              })
            ) : (
              <div className={styles['time-selection__no-timeslots']}>
                <Text
                  text={diningDateTimeProps.noTimeslotsLabel}
                  styleOption="body-small"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimeSelectionStep;
